import { api } from '@hunk/shared/api';
import type { LoginRes } from '@hunk/shared/api/apiAuth';
import { FetchApiError } from '@hunk/shared/api/fetchApi';
import { ErrorCodes, type ErrorCodeType } from '@hunk/shared/apiDataErrorCode';
import { IS_MINI_APP, TG_INIT_DATA } from '@/appMode';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Session = Required<Pick<LoginRes, 'token'>> & Omit<LoginRes, 'errorCode' | 'errorText' | 'token'>;
type Status = 'loading' | 'anonymous' | 'authenticated';

interface SessionContextType {
  status: Status;
  session: Session | undefined;
  /** last login problem, shown on the login page */
  error: string | undefined;
  /** running as the Mini App (/mini) */
  isMiniApp: boolean;
  /** Mini App opened outside Telegram (no initData) */
  missingInitData: boolean;
  authHeaders: () => HeadersInit;
  /** email login step 1: sends the code; returns seconds until a new code may be asked, or false + error */
  requestEmailCode: (email: string, captcha: string) => Promise<number | false>;
  /** email login step 2 */
  verifyEmailCode: (email: string, code: string) => Promise<boolean>;
  logout: () => void;
  /** retry the start-up login (Mini App) */
  relogin: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);
const TOKEN_KEY = 'hunk.session';

/** the website keeps its session in localStorage; the Mini App never does (initData logs in every launch) */
const store = {
  get: () => {
    if (IS_MINI_APP) return null;
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string) => {
    if (IS_MINI_APP) return;
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // private mode - the session lasts until the tab closes
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
  }
};

/** 125 -> "2:05" (minutes:seconds) */
export const waitText = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

export const errorText = (err: unknown): string => {
  if (err instanceof FetchApiError) {
    const code = err.responseError.errorCode;
    return err.responseError.errorText ?? (code ? ErrorCodes[code] : undefined) ?? err.message;
  }
  return err instanceof Error ? err.message : ErrorCodes.UnexpectedError;
};

/** the website Telegram login comes back as /#session=... or #login_error=...; take it and clean the address bar */
function takeFromHash(): { token?: string; error?: string } {
  const h = new URLSearchParams(window.location.hash.slice(1));
  const token = h.get('session') ?? undefined;
  const code = h.get('login_error') ?? undefined;
  if (!token && !code) return {};
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return { token, error: code ? (ErrorCodes[code as ErrorCodeType] ?? code) : undefined };
}

/**
 * Mini App (/mini): only Telegram initData - automatic; without it, ask to open from the bot.
 * Website (/): back from "Log in with Telegram" (#session=...), a session saved in this browser, or anonymous
 * (the shop is open to everyone; checkout asks to log in with Telegram or an email code).
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<Session>();
  const [error, setError] = useState<string>();
  const isMiniApp = IS_MINI_APP;
  const initDataRaw = TG_INIT_DATA;
  const missingInitData = isMiniApp && initDataRaw === '';

  const accept = useCallback((res: LoginRes) => {
    if (!res.token) throw new Error(ErrorCodes.UnexpectedError);
    store.set(res.token);
    setSession({ ...res, token: res.token });
    setError(undefined);
    setStatus('authenticated');
  }, []);

  /** a saved / returned token: check it with the server and load the user */
  const resume = useCallback(
    async (token: string) => {
      const { response } = await api.selfUser.getMyInfo({ headers: { authorization: `Bearer ${token}` } });
      accept({ token, user: response.user, role: response.role });
    },
    [accept]
  );

  const relogin = useCallback(async () => {
    setStatus('loading');
    try {
      if (isMiniApp) {
        if (missingInitData) return setStatus('anonymous');
        return accept((await api.auth.login({ data: { initDataRaw } })).response);
      }
      const fromHash = takeFromHash();
      if (fromHash.error) throw new Error(fromHash.error);
      if (fromHash.token) return await resume(fromHash.token);
      const saved = store.get();
      if (saved) return await resume(saved);
      setStatus('anonymous');
    } catch (err) {
      store.clear();
      setSession(undefined);
      setError(errorText(err));
      setStatus('anonymous');
    }
  }, [accept, resume, isMiniApp, missingInitData, initDataRaw]);

  useEffect(() => {
    void relogin();
  }, [relogin]);

  const requestEmailCode = useCallback(async (email: string, captcha: string) => {
    try {
      setError(undefined);
      return (await api.auth.requestEmailCode({ data: { email, captcha } })).response.resendAfterSec ?? 180;
    } catch (err) {
      const wait = err instanceof FetchApiError ? (err.responseError as { resendAfterSec?: number }).resendAfterSec : undefined;
      setError(wait ? `לכתובת הזו כבר נשלח קוד. אפשר לבקש קוד חדש בעוד ${waitText(wait)}` : errorText(err));
      return false;
    }
  }, []);

  const verifyEmailCode = useCallback(
    async (email: string, code: string) => {
      try {
        accept((await api.auth.verifyEmailCode({ data: { email, code } })).response);
        return true;
      } catch (err) {
        setError(errorText(err));
        return false;
      }
    },
    [accept]
  );

  const logout = useCallback(() => {
    store.clear();
    setSession(undefined);
    setError(undefined);
    setStatus('anonymous');
  }, []);

  const authHeaders = useCallback((): HeadersInit => (session?.token ? { authorization: `Bearer ${session.token}` } : {}), [session?.token]);

  const value = useMemo(
    () => ({ status, session, error, isMiniApp, missingInitData, authHeaders, requestEmailCode, verifyEmailCode, logout, relogin }),
    [status, session, error, isMiniApp, missingInitData, authHeaders, requestEmailCode, verifyEmailCode, logout, relogin]
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextType {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
